import Konva from "konva";
import {DoorCorners, Point} from "./types";

export type CornerPosition = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
type CornerPositionClipped = 'topLeftBottom' | 'topLeftRight' | 'topRightBottom' | 'topRightLeft'
export const AllCornerPositions: Array<CornerPosition> = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight']
export type CornerPositionWithClipped = CornerPosition | CornerPositionClipped

const CornerPositionsClipped: Array<CornerPositionClipped> = ['topLeftBottom', 'topLeftRight', 'topRightBottom', 'topRightLeft']

/**
 * Represents a door corner refinement of a single set of door corners
 */
export class DoorRefinement {

  private group: Konva.Group;

  private pointsArray: Array<Point>
  private cornerRefinements: Array<CornerRefinement> = []

  private cornerRefinementsClipped: {
    topLeftBottom?: CornerRefinement
    topLeftRight?: CornerRefinement
    topRightBottom?: CornerRefinement
    topRightLeft?: CornerRefinement

    leftLine?: Konva.Line
    rightLine?: Konva.Line
  } = {}

  private moved: { x: number, y: number } = {x: 0, y: 0}

  private indexText: Konva.Text | undefined
  private deleteButtonGroup: Konva.Group | undefined
  private lines: Konva.Line

  constructor(
    private readonly layer: Konva.Layer,
    private readonly doorCorners: DoorCorners,
    private isSelected: boolean,
    private readonly options: {
      primaryColor: string
      strokeWidth?: number
      selectable?: boolean
      notEditable?: boolean
      showIndex?: boolean
      deleteable?: boolean
      showDeleteButtonWithoutHover?: boolean
      scale?: (number: number) => number
    },
    private readonly changeCursor: (cursor: string) => void,
    private readonly events: {
      onCornerFocus: (doorCorners: DoorCorners, position: CornerPositionWithClipped) => void,
      onUnFocus: () => void,
      onPositionChanged: (doorCorners: DoorCorners) => void,
      onCornerPositionChanged: (doorCorners: DoorCorners, position: CornerPositionWithClipped) => void,
      onSelected?: () => void
      onDeleted: () => void
    }
  ) {
    this.pointsArray = convertCornersToPointsArray(doorCorners)

    // Dragable group for the whole door
    this.group = new Konva.Group({
      draggable: !options.notEditable,
    });

    // Onclick handler for whole group
    if (this.options.selectable) {
      this.group.on('click', (e) => {
        if (events.onSelected) {
          events.onSelected()
        }
        e.cancelBubble = true;
      })
    }

    // Mouse up handler for whole group
    this.group.on('mouseup touchend dragend', (e) => {
      events.onUnFocus()
    })

    const rgbColor = this.hexToRgb(this.options.primaryColor)

    // Create rectangle line
    this.lines = new Konva.Line({
      points: this.pointsArray.map(c => [c.x, c.y]).flat(),
      stroke: this.isSelected ? this.options.primaryColor : 'white',
      closed: true,
      strokeWidth: this.options.strokeWidth || 3,
      draggable: false,
    });
    this.group.add(this.lines);

    if (this.doorCorners.hasClippedCorners) {
      this.addClippedCornerControlls(layer)
    }

    // Add index Text
    if (options.showIndex) {
      const fontSize = 40
      this.indexText = new Konva.Text({
        x: 0,
        y: 0,
        text: `${doorCorners.index}`,
        fontSize,
        fill: isSelected ? this.options.primaryColor : 'lightgrey',
      })
      this.updateIndexTextPosition()
      this.group.add(this.indexText)
    }

    if (!this.options.notEditable && this.options.deleteable) {
      this.addDeleteButton()
    }

    if (!this.options.notEditable) {
      // Listen for drag event on whole group
      this.group.on('dragmove', (e) => {
        this.moved.x = e.target.attrs.x
        this.moved.y = e.target.attrs.y
        events.onPositionChanged(this.getDoorCorners())
        this.updateClippedCornerLines()
      })
    }

    this.group.on('mouseover', () => {

      if (this.options.selectable && !this.options.notEditable) {
        this.changeCursor('pointer');
      }

      if (!this.options.showDeleteButtonWithoutHover && this.deleteButtonGroup) {
        this.deleteButtonGroup.opacity(1)
      }

      if (!this.isSelected && this.options.selectable) {
        this.lines.fill(`rgba(${rgbColor},0.2)`)
      }
    });

    this.group.on('mouseout', () => {
      if (this.options.selectable && !this.options.notEditable) {
        this.changeCursor('default');
      }

      if (!this.options.showDeleteButtonWithoutHover && this.deleteButtonGroup) {
        this.deleteButtonGroup.opacity(0)
      }

      if (!this.isSelected && !this.isSelected) {
        this.lines.fill(`rgba(${rgbColor},0.0)`)
      }
    });

    // Create corner refinements
    if (!this.options.notEditable) {
      for (const position of AllCornerPositions) {
        const cornerRefinement = new CornerRefinement({
          point: this.doorCorners[position],
          position: position as CornerPosition,
          doorCornersIndex: doorCorners.index,
          layer,
          options: this.options,
          changeCursor,
          onPositionChanged: (newPoint) => {

            let ratios: { LB?: number, LR?: number, RL?: number, RB?: number } = {}

            if (this.doorCorners.hasClippedCorners) {
              ratios = {
                LB: this.distanceBetweenPoints(this.doorCorners.topLeft, this.doorCorners.topLeftBottom!)
                  / this.distanceBetweenPoints(this.doorCorners.topLeft, this.doorCorners.bottomLeft),
                LR: this.distanceBetweenPoints(this.doorCorners.topLeft, this.doorCorners.topLeftRight!)
                  / this.distanceBetweenPoints(this.doorCorners.topLeft, this.doorCorners.topRight!),
                RL: this.distanceBetweenPoints(this.doorCorners.topRight, this.doorCorners.topRightLeft!)
                  / this.distanceBetweenPoints(this.doorCorners.topRight, this.doorCorners.topLeft!),
                RB: this.distanceBetweenPoints(this.doorCorners.topRight, this.doorCorners.topRightBottom!)
                  / this.distanceBetweenPoints(this.doorCorners.topRight, this.doorCorners.bottomRight!)
              }
            }

            // Update the door corners with the new point
            this.doorCorners[position] = newPoint;

            // Update the rectangle line
            this.pointsArray = convertCornersToPointsArray(doorCorners)
            this.lines.points(this.pointsArray.map(c => [c.x, c.y]).flat());

            // Update clipped corners
            if (this.doorCorners.hasClippedCorners && ratios.LB && ratios.LR && ratios.RL && ratios.RB) {
              this.doorCorners.topLeftBottom =  this.pointOnLine({
                point1: this.doorCorners.topLeft,
                point2: this.doorCorners.bottomLeft,
                ratio: ratios.LB
              })
              this.doorCorners.topLeftRight =  this.pointOnLine({
                point1: this.doorCorners.topLeft,
                point2: this.doorCorners.topRight,
                ratio: ratios.LR
              })
              this.doorCorners.topRightLeft = this.pointOnLine({
                point1: this.doorCorners.topRight,
                point2: this.doorCorners.topLeft,
                ratio: ratios.RL
              })
              this.doorCorners.topRightBottom = this.pointOnLine({
                point1: this.doorCorners.topRight,
                point2: this.doorCorners.bottomRight,
                ratio: ratios.RB
              })

              // Update the corner refinement points
              this.cornerRefinementsClipped.topLeftBottom!.getGroup().position(this.doorCorners.topLeftBottom)
              this.cornerRefinementsClipped.topLeftRight!.getGroup().position(this.doorCorners.topLeftRight)
              this.cornerRefinementsClipped.topRightLeft!.getGroup().position(this.doorCorners.topRightLeft)
              this.cornerRefinementsClipped.topRightBottom!.getGroup().position(this.doorCorners.topRightBottom)

              this.updateClippedCornerLines()
            }

            events.onCornerPositionChanged(this.getDoorCorners(), position)
            this.updateIndexTextPosition()
            this.updateDeleteButtonPosition()
          },
          onFocus: () => events.onCornerFocus(this.getDoorCorners(), position),
          onUnFocus: () => events.onUnFocus()
        });
        this.cornerRefinements.push(cornerRefinement);
        this.group.add(cornerRefinement.getGroup());
      }
    } else {
      // Only show a small dot
      for (const position of AllCornerPositions) {
        const dot = new Konva.Circle({
          radius: 5,
          fill: this.options.primaryColor,
          x: this.doorCorners[position].x,
          y: this.doorCorners[position].y,
        })

        this.group.add(dot)
      }
    }
  }

  private distanceBetweenPoints(p1: Point, p2: Point): number {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
  }

  private pointOnLine({ point1, point2, ratio }: {
    point1: Point;
    point2: Point;
    ratio: number;
  }): Point {
    // Calculate the coordinates of the point on the line using the ratio
    return {
      x: point1.x + (point2.x - point1.x) * ratio,
      y: point1.y + (point2.y - point1.y) * ratio
    }
  }

  private updateDeleteButtonPosition(): void {
    if (!this.deleteButtonGroup) {
      return
    }
    const centerX = (this.doorCorners.topRight.x + this.doorCorners.topLeft.x) / 2;
    const centerY = (this.doorCorners.topRight.y + this.doorCorners.topLeft.y) / 2;

    this.deleteButtonGroup.x(centerX - 10)
    this.deleteButtonGroup.y(centerY + 10)
  }

  private addClippedCornerControlls(layer: Konva.Layer): void {
    const t = 0.2; // Example: 0.5 represents the midpoint

    const clippedCornersOptions: Array<{
      position: CornerPositionClipped
      connectedCorners: Array<CornerPosition>
    }> = [
      {
        position: 'topLeftRight',
        connectedCorners: ['topLeft', 'topRight'],
      },
      {
        position: 'topLeftBottom',
        connectedCorners: ['topLeft', 'bottomLeft'],
      },

      {
        position: 'topRightBottom',
        connectedCorners: ['topRight', 'bottomRight'],
      },
      {
        position: 'topRightLeft',
        connectedCorners: ['topRight', 'topLeft'],
      }
    ]

    // Minimum distance between the corners
    const minimumDistanceRatio = 0.1

    for (const cornerOptions of clippedCornersOptions) {

      const corner1 = this.doorCorners[cornerOptions.connectedCorners[0]]
      const corner2 = this.doorCorners[cornerOptions.connectedCorners[1]]
      this.doorCorners[cornerOptions.position] = this.doorCorners[cornerOptions.position] !== undefined ? this.doorCorners[cornerOptions.position] : this.pointOnLine({ point1: corner1, point2: corner2, ratio: t })
      const clippedTopLeftRight = new CornerRefinement({
        point: this.doorCorners[cornerOptions.position]!,
        doorCornersIndex: this.doorCorners.index,
        position: cornerOptions.position,
        layer,
        options: {
          scale: this.options.scale,
          primaryColor: this.options.primaryColor,
          dragBoundFunc: (pos: Point) => {

            // On the top line the clipped corners shouldn't be allowed to cross each other
            // There should be a minimum distance between them of 0.1 of the line length
            let restriction1, restriction2
            if (cornerOptions.position === 'topLeftRight') {
              restriction1 = this.doorCorners['topLeft']
              restriction2 = this.doorCorners['topRight']

              // It shouldn't be allowed to touch the other corner directly
              restriction2 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: 0.5 - (minimumDistanceRatio/2)
              })

              restriction1 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: minimumDistanceRatio
              })

            } else if (cornerOptions.position === 'topRightLeft') {
              restriction1 = this.doorCorners['topLeft']
              restriction2 = this.doorCorners['topRight']

              // It shouldn't be allowed to touch the other corner directly
              restriction1 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: 0.5 + (minimumDistanceRatio/2)
              })

              restriction2 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: 1 - minimumDistanceRatio
              })
            } else {
              // On the left or right line we can just use the whole line as restriction
              restriction1 = this.doorCorners[cornerOptions.connectedCorners[0]]
              restriction2 = this.doorCorners[cornerOptions.connectedCorners[1]]

              // It shouldn't be allowed to touch the other corner directly
              restriction1 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: minimumDistanceRatio
              })
              restriction2 = this.pointOnLine({
                point1: restriction1,
                point2: restriction2,
                ratio: 0.5
              })
            }

            const result = this.getProjectedPointOnLineSegment(pos, restriction1, restriction2)

            result.x = this.moved.x + result.x
            result.y = this.moved.y + result.y

            return this.scalePoint(result)
          }
        },
        changeCursor: this.changeCursor,
        onPositionChanged: (newPoint) => {
          // Update the door corners with the new point
          this.doorCorners[cornerOptions.position] = newPoint;
          this.events.onCornerPositionChanged(this.getDoorCorners(), cornerOptions.position)
          this.updateClippedCornerLines()
        },
        onFocus: () => {
          this.events.onCornerFocus(this.getDoorCorners(), cornerOptions.position)
        },
        onUnFocus: () => this.events.onUnFocus()
      });
      this.cornerRefinementsClipped[cornerOptions.position] = clippedTopLeftRight;
      this.group.add(clippedTopLeftRight.getGroup());
    }

    // Line between clipped points
    const lineTopLeft = new Konva.Line({
      points: [this.cornerRefinementsClipped.topLeftRight!.getGroup().x(), this.cornerRefinementsClipped.topLeftRight!.getGroup().y(), this.cornerRefinementsClipped.topLeftBottom!.getGroup().x(), this.cornerRefinementsClipped.topLeftBottom!.getGroup().y()],
      stroke: this.options.primaryColor,
      strokeWidth: this.options.strokeWidth || 3,
      draggable: false,
    });
    this.cornerRefinementsClipped.leftLine = lineTopLeft;
    this.group.add(lineTopLeft);

    const lineTopRight = new Konva.Line({
      points: [this.cornerRefinementsClipped.topRightLeft!.getGroup().x(), this.cornerRefinementsClipped.topRightLeft!.getGroup().y(), this.cornerRefinementsClipped.topRightBottom!.getGroup().x(), this.cornerRefinementsClipped.topRightBottom!.getGroup().y()],
      stroke: this.options.primaryColor,
      strokeWidth: this.options.strokeWidth || 3,
      draggable: false,
    });
    this.cornerRefinementsClipped.rightLine = lineTopRight;
    this.group.add(lineTopRight);
  }

  private getProjectedPointOnLineSegment(p: Point, a: Point, b: Point): Point {
    const atob = { x: b.x - a.x, y: b.y - a.y };
    const atop = { x: p.x - a.x, y: p.y - a.y };
    const len = atob.x * atob.x + atob.y * atob.y;
    const dot = atop.x * atob.x + atop.y * atob.y;
    const t = Math.min(1, Math.max(0, dot / len));
    return {
      x: a.x + atob.x * t,
      y: a.y + atob.y * t
    };
  }

  private scaleNumber(number: number): number {
    return this.options.scale ? this.options.scale(number) : number
  }

  private scalePoint(point: Point): Point {
    return {
      x: this.scaleNumber(point.x),
      y: this.scaleNumber(point.y)
    }
  }

  private addDeleteButton(): void {
    this.deleteButtonGroup = new Konva.Group({
      opacity: this.options.showDeleteButtonWithoutHover ? 1 : 0,
    })

    const buttonRect = new Konva.Rect({
      width: 26,
      height: 26,
      fill: this.options.primaryColor,
      cornerRadius: 5,
      strokeWidth: 1,
    })
    this.updateDeleteButtonPosition()
    this.deleteButtonGroup.add(buttonRect)

    const buttonText = new Konva.Text({
        text: 'x',
        fontSize: 15,
        fill: 'black',
        width: 20,
        height: 20,
        x: 9,
        y: 5
    })
    this.deleteButtonGroup.add(buttonText)

    // Change color on mouseover
    this.deleteButtonGroup.on('mouseover', (e) => {
      buttonRect.fill('red')
    })
    this.deleteButtonGroup.on('mouseout', (e) => {
      buttonRect.fill(this.options.primaryColor)
    })

    // Click handler for mobile and desktop
    this.deleteButtonGroup.on('tap', (e) => {
      this.events.onDeleted()
      e.cancelBubble = true;
    })

    this.deleteButtonGroup.on('click', (e) => {
      this.events.onDeleted()
      e.cancelBubble = true;
    })

    this.group.add(this.deleteButtonGroup)
  }

  private updateIndexTextPosition(): void {
    if (!this.indexText) {
      return
    }
    const center = this.getCenter()
    this.indexText.x(center.x - this.indexText.width() / 2)
    this.indexText.y(center.y - this.indexText.height() / 2)
  }

  private hexToRgb(hex: string): string | undefined {
    // Remove any leading '#' character if present
    hex = hex.replace(/^#/, '');

    // Expand short hex strings (3 characters) to full format (6 characters)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return `${r},${g},${b}`
  }

  public afterInit(): void {
    for (const cornerRefinement of this.cornerRefinements) {
      cornerRefinement.addTweens()
    }
    if (this.cornerRefinementsClipped.topLeftBottom) {
      this.cornerRefinementsClipped.topLeftBottom.addTweens()
    }
    if (this.cornerRefinementsClipped.topLeftRight) {
      this.cornerRefinementsClipped.topLeftRight.addTweens()
    }
    if (this.cornerRefinementsClipped.topRightBottom) {
      this.cornerRefinementsClipped.topRightBottom.addTweens()
    }
    if (this.cornerRefinementsClipped.topRightLeft) {
      this.cornerRefinementsClipped.topRightLeft.addTweens()
    }
  }

  public setSelected(isSelected: boolean): void {
    this.isSelected = isSelected
    // this.indexText.fill(isSelected ? this.options.primaryColor : 'lightgrey')
    this.lines.stroke(isSelected ? this.options.primaryColor : 'white')
    this.lines.fill(`rgba(${this.hexToRgb(this.options.primaryColor)},${isSelected ? 0.2 : 0.0})`)
  }

  public getIndex(): number {
    return this.doorCorners.index
  }

  public getGroup(): Konva.Group {
    return this.group
  }

  public getDoorCorners(): DoorCorners {
    // @ts-expect-error Door corners are defined below
    const doorCorners: DoorCorners = {
      index: this.doorCorners.index,
      doorType: this.doorCorners.doorType,
      hasClippedCorners: this.doorCorners.hasClippedCorners
    }

    for (const position of AllCornerPositions) {
      doorCorners[position] = {
        x: this.doorCorners[position].x + this.moved.x,
        y: this.doorCorners[position].y + this.moved.y,
      }
    }

    if (doorCorners.hasClippedCorners) {

      for (const position of CornerPositionsClipped) {

        const existingCorner = this.doorCorners[position]
        if (!existingCorner) {
          console.warn(`Corner ${position} does not exist`)
          continue
        }

        doorCorners[position] = {
          x: existingCorner!.x + this.moved.x,
          y: existingCorner!.y + this.moved.y,
        }
      }
    }

    return doorCorners
  }

  private updateClippedCornerLines(): void {

    if (!this.cornerRefinementsClipped.topLeftBottom || !this.cornerRefinementsClipped.topLeftRight
    || !this.cornerRefinementsClipped.topRightBottom || !this.cornerRefinementsClipped.topRightLeft) {
      return
    }


    const topLeft = this.cornerRefinementsClipped.topLeftBottom!.getGroup().position()
    const topRight = this.cornerRefinementsClipped.topLeftRight!.getGroup().position()

    this.cornerRefinementsClipped.leftLine!.points([topLeft.x, topLeft.y, topRight.x, topRight.y])

    const topRight2 = this.cornerRefinementsClipped.topRightLeft!.getGroup().position()
    const bottomRight = this.cornerRefinementsClipped.topRightBottom!.getGroup().position()
    this.cornerRefinementsClipped.rightLine!.points([topRight2.x, topRight2.y, bottomRight.x, bottomRight.y])
  }

  private getCenter(): Point {
    const points = this.pointsArray

    // Calculate the average of x-coordinates and y-coordinates
    const centerX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
    const centerY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;

    // Return the center as a Point object
    return {x: centerX, y: centerY};
  }
}

/**
 * Represents a corner refinement point
 */
class CornerRefinement {
  private group: Konva.Group;
  private point: Point;
  private layer: Konva.Layer;
  private position: CornerPositionWithClipped
  private options: {
    primaryColor: string
    scale?: (number: number) => number
  }
  private innerCircle: Konva.Circle;
  private outerCircle: Konva.Circle;

  private stage: Konva.Stage;
  private dragOffset: Point = { x: 0, y: 0 }

  private onPositionChanged: (n: Point) => void;
  private onFocus: () => void;
  private onUnFocus: () => void;
  private changeCursor: (cursor: string) => void;

  constructor({point, layer, options, onPositionChanged, position, onFocus, onUnFocus, changeCursor, doorCornersIndex}: {
    point: Point,
    layer: Konva.Layer,
    position: CornerPositionWithClipped
    doorCornersIndex: number
    options: {
      primaryColor: string
      dragBoundFunc?: (pos: Point) => Point
      scale?: (number: number) => number
    },
    changeCursor: (cursor: string) => void,
    onPositionChanged: (n: Point) => void
    onFocus: () => void
    onUnFocus: () => void
  }) {
    this.onPositionChanged = onPositionChanged
    this.point = point;
    this.layer = layer;
    this.position = position
    this.options = options
    this.stage = layer.getStage()
    this.onFocus = onFocus
    this.onUnFocus = onUnFocus
    this.changeCursor = changeCursor

    // TODO add drag bounds of stage
    this.group = new Konva.Group({
      x: point.x,
      y: point.y,
      height: 40,
      width: 40,
      draggable: true,
      blurRadius: 50,
      id: `${doorCornersIndex}-${position}`,
      dragBoundFunc: options.dragBoundFunc ? (point) => {
        return options.dragBoundFunc!({
          x: point.x + this.dragOffset.x,
          y: point.y + this.dragOffset.y,
        })
      }: undefined,
    });

    // Outer circle
    this.outerCircle = new Konva.Circle({
      radius: 35,
      width: 30,
      height: 30,
      fill: 'rgb(219, 219, 219,0.7)',
      stroke: options.primaryColor,
      strokeWidth: 1,
    });
    this.group.add(this.outerCircle);

    // Add inner circle
    this.innerCircle = new Konva.Circle({
      radius: 10,
      width: 8,
      height: 8,
      fill: options.primaryColor,
    });
    this.group.add(this.innerCircle);

    this.group.on('dragstart', (e) => {
      const pointer = this.stage.getPointerPosition();
      if (!pointer) {
        this.dragOffset = { x: 0, y: 0 };
        return;
      }

      const pos = this.group.position();
      this.dragOffset = {
        x: Math.abs(pointer.x - pos.x),
        y: Math.abs(pointer.y - pos.y),
      };
    })

    this.group.on('dragmove', (e) => {
      this.onPositionChanged({
        x: e.target.attrs.x,
        y: e.target.attrs.y
      });
      e.cancelBubble = true;
    })
  }

  public addTweens(): void {
    const mouseoverTweenOuter = new Konva.Tween({
      node: this.outerCircle,
      duration: 0.2,
      radius: 25,
      strokeWidth: 2
    });

    const dragTweenInner = new Konva.Tween({
      node: this.innerCircle,
      duration: 0.1,
      radius: 5,
      fill: 'white',
    });

    const dragTweenOuter = new Konva.Tween({
      node: this.outerCircle,
      duration: 0.1,
      stroke: 'white',
    });

    // change color of outer circle on dragstart
    this.group.on('mousedown touchstart', () => {
      dragTweenInner.play();
      dragTweenOuter.play();
      //this.visualizeEngine.setRenderDoor(false);
      this.onFocus();
    });

    // change the color back on dragend
    this.group.on('mouseup touchend dragend', async () => {
      dragTweenInner.reverse();
      dragTweenOuter.reverse();

      // to rerender door after drop
      // TODO: if door should be rendered during refinement, this implementation needs to change completely
      //await this.scenarioImageService.updateCorners(this.visualizeEngine.convertScreenPositionToScenarioPosition(this.corners!));
      //this.visualizeEngine.setRenderDoor(true);

      this.onUnFocus();
    });

    // add cursor styling on mouseover
    this.group.on('mouseover', () => {
      this.changeCursor('pointer');
      mouseoverTweenOuter.play();
    });

    this.group.on('mouseout', () => {
      this.changeCursor('default');
      mouseoverTweenOuter.reverse();
    });
  }

  public getGroup(): Konva.Group {
    return this.group;
  }
}

export function convertCornersToPointsArray(doorCorners: DoorCorners): Array<Point> {
  return [doorCorners.topLeft, doorCorners.topRight, doorCorners.bottomRight, doorCorners.bottomLeft]
}
